import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, cents, day, useCurrentCompany, ApiError } from '../../lib'
import { errorFrom, networkError, readCookie } from '../../lib/api'
import { Empty, ErrorBox, Loading, NoCompany } from '../../app/ui'
import { PageBand } from '../../app/PageBand'

type Invoice = {
  id: string; vendor: string; status: string; reason: string | null; invoice_number: string | null
  invoice_date: string | null; amount_cents: number | null; earning_cents?: number | null; via: string; created_at: string
}

type Inbox = { id: string; provider: 'google' | 'microsoft'; label: string; mailbox: string; status: string; last_checked_at: string | null; last_error: string | null }
type Inboxes = { rows: Inbox[]; providers: { id: 'google' | 'microsoft'; label: string }[]; admin_consent_url: string | null }

// What the page says when connecting an inbox ends early (pr/inbox/routes.py WHY).
const INBOX_NOTE: Record<string, string> = {
  denied: 'Nothing was connected, because access was not allowed.',
  taken: 'That inbox is already connected to another company.',
  expired: 'That took too long, so nothing was connected.',
  failed: 'Could not connect that inbox.',
  signed_out: 'You were signed out while connecting, so nothing was connected.',
}

const LABEL: Record<string, string> = {
  counted: 'Earning', needs_review: 'Being checked', rejected: 'Not counted', duplicate: 'Already sent', received: 'Reading',
}

async function upload(companyId: string, vendor: string, file: File): Promise<Invoice> {
  let res: Response
  try {
    res = await fetch(`/api/companies/${companyId}/invoices?vendor=${vendor}`, {
      method: 'POST', credentials: 'same-origin', body: file,
      headers: { 'content-type': file.type || 'application/pdf', 'x-filename': file.name, 'X-PR-CSRF': readCookie('pr_csrf') ?? '' },
    })
  } catch {
    throw networkError()
  }
  const data = await res.json().catch((e) => {
    if (e instanceof SyntaxError) return {}
    throw networkError()
  })
  if (!res.ok) throw errorFrom(res.status, data)
  return data as Invoice
}

const POOL = 4

type Outcome = { name: string; kind: 'added' | 'skipped' | 'failed'; reason?: string }
type Summary = { added: number; skipped: number; failed: { name: string; reason: string }[] }

async function sendOne(companyId: string, vendor: string, file: File): Promise<Outcome> {
  try {
    const inv = await upload(companyId, vendor, file)
    return { name: file.name, kind: inv.status === 'duplicate' ? 'skipped' : 'added' }
  } catch (e) {
    if (e instanceof ApiError && e.code === 'duplicate_file') return { name: file.name, kind: 'skipped' }
    const reason = e instanceof ApiError ? e.message || e.code : e instanceof Error ? e.message : 'Something went wrong'
    return { name: file.name, kind: 'failed', reason }
  }
}

/** Use SHA-256 when available; otherwise only the same File object proves a repeat. */
async function fileKey(file: File): Promise<string | File> {
  if (crypto.subtle) {
    try {
      const hash = await crypto.subtle.digest('SHA-256', await file.arrayBuffer())
      return Array.from(new Uint8Array(hash), (b) => b.toString(16).padStart(2, '0')).join('')
    } catch { /* fall back below */ }
  }
  return file
}

/** Send files a few at a time. One bad file never stops the rest. A repeat in the same batch is skipped here; the
 * server still refuses a file it already has (duplicate_file). */
async function sendAll(companyId: string, vendor: string, files: File[], onDone: (n: number) => void): Promise<Summary> {
  const out: Outcome[] = []
  const seen = new Set<string | File>()
  const unique: File[] = []
  for (const f of files) {
    const key = await fileKey(f)
    if (seen.has(key)) out.push({ name: f.name, kind: 'skipped' })
    else { seen.add(key); unique.push(f) }
  }
  if (out.length) onDone(out.length)
  let next = 0
  const worker = async () => {
    while (next < unique.length) {
      const f = unique[next++]
      out.push(await sendOne(companyId, vendor, f))
      onDone(out.length)
    }
  }
  await Promise.all(Array.from({ length: Math.min(POOL, unique.length) }, worker))
  return {
    added: out.filter((o) => o.kind === 'added').length,
    skipped: out.filter((o) => o.kind === 'skipped').length,
    failed: out.filter((o) => o.kind === 'failed').map((o) => ({ name: o.name, reason: o.reason ?? '' })),
  }
}

function summaryLine(s: Summary): string {
  const parts: string[] = []
  if (s.added) parts.push(`${s.added} added`)
  if (s.skipped) parts.push(`${s.skipped} already sent`)
  if (s.failed.length) parts.push(`${s.failed.length} failed`)
  return parts.join(', ')
}

/** /invoices: OpenAI and Anthropic receipts earning 4 points per $1 (Stitch promotion, pr/onboarding/invoices). */
export function InvoicesPage() {
  const company = useCurrentCompany()
  const qc = useQueryClient()
  const [vendor, setVendor] = useState<'openai' | 'anthropic'>('openai')
  const [params, setParams] = useSearchParams()
  const note = params.get('inbox')
  const noteProvider = params.get('provider') === 'microsoft' ? 'microsoft' : 'google'
  const inboxes = useQuery({
    queryKey: ['inboxes', company?.id],
    queryFn: () => api<Inboxes>('GET', `/api/companies/${company!.id}/inboxes`),
    enabled: !!company,
    // A new inbox is read straight away; keep looking until its first check is done.
    refetchInterval: (q) => (q.state.data?.rows.some((r) => r.status === 'active' && !r.last_checked_at) ? 2000 : false),
  })
  const waiting = inboxes.data?.rows.some((r) => r.status === 'active' && !r.last_checked_at)
  // Whenever an inbox has just been checked, load the list again so the receipts it found show.
  const checked = (inboxes.data?.rows ?? []).map((r) => r.last_checked_at ?? '').join(',')
  useEffect(() => { if (checked) qc.invalidateQueries({ queryKey: ['invoices'] }) }, [checked, qc])
  const list = useQuery({
    queryKey: ['invoices', company?.id],
    queryFn: () => api<{ rows: Invoice[] }>('GET', `/api/companies/${company!.id}/invoices`),
    enabled: !!company,
    refetchInterval: waiting ? 2000 : false,    // receipts read from a new inbox appear on their own
  })
  const connect = useMutation({
    mutationFn: (provider: string) => api<{ url: string }>('POST', `/api/companies/${company!.id}/inboxes/${provider}/start`),
    onSuccess: (r) => { window.location.assign(r.url) },
  })
  const remove = useMutation({
    mutationFn: (id: string) => api('POST', `/api/inboxes/${id}/remove`),
    onSuccess: () => setParams({}, { replace: true }),    // the old "Connected" line no longer holds
    onSettled: () => qc.invalidateQueries({ queryKey: ['inboxes'] }),
  })
  const [progress, setProgress] = useState({ done: 0, total: 0 })
  const send = useMutation({
    mutationFn: (files: File[]) => {
      setProgress({ done: 0, total: files.length })
      return sendAll(company!.id, vendor, files, (done) => setProgress((p) => ({ ...p, done })))
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['invoices'] }),
  })
  const result = send.data
  if (!company) return <NoCompany />

  return (
    <div className="page stack" data-testid="invoices">
      <PageBand title="Receipts" sub="OpenAI and Anthropic earn 4 points per $1 of API spend, including last month's. Plans such as ChatGPT Plus or Claude Max earn nothing." />
      <section className="e-panel stack" style={{ gap: '0.75rem' }} data-testid="inbox">
        <h2 className="e-heading">Connect the inbox your OpenAI and Anthropic receipts go to</h2>
        <p className="muted" data-testid="inbox-notice">ernd reads only receipt PDFs from openai.com and anthropic.com. Other mail isn't opened.</p>
        <div className="row">
          {(inboxes.data?.providers ?? []).map((p) => (
            <button key={p.id} type="button" className="button primary" data-testid={`inbox-connect-${p.id}`}
              disabled={connect.isPending} onClick={() => connect.mutate(p.id)}>
              Connect {p.label}
            </button>
          ))}
        </div>
        {note === 'connected' ? <p data-testid="inbox-note">Connected. Reading your receipts now.</p> : null}
        {note === 'admin' ? (
          <p role="alert" data-testid="inbox-admin">
            Your Microsoft admin needs to allow ernd. Send them this link.{' '}
            {inboxes.data?.admin_consent_url ? <a href={inboxes.data.admin_consent_url} data-testid="inbox-admin-link">{inboxes.data.admin_consent_url}</a> : null}
          </p>
        ) : note && INBOX_NOTE[note] ? (
          <div className="row" role="alert">
            <p data-testid="inbox-note">{INBOX_NOTE[note]}</p>
            <button type="button" data-testid="inbox-retry" disabled={connect.isPending}
              onClick={() => connect.mutate(noteProvider)}>Try again</button>
          </div>
        ) : null}
        {connect.error ? <ErrorBox error={connect.error} /> : null}
        {remove.error ? <ErrorBox error={remove.error} /> : null}
        {(inboxes.data?.rows ?? []).map((i) => (
          <div className="row" key={i.id} data-testid={`inbox-row-${i.mailbox}`}>
            <strong>{i.mailbox}</strong>
            <span className="muted">
              {i.status === 'needs_reconnect'
                ? `${i.label} no longer lets ernd read this inbox.`
                : i.last_checked_at ? `Last checked ${day(i.last_checked_at)}` : 'Checking…'}
            </span>
            {i.status === 'needs_reconnect' ? (
              <button type="button" data-testid="inbox-reconnect" onClick={() => connect.mutate(i.provider)}>Reconnect</button>
            ) : null}
            <button type="button" data-testid="inbox-remove" onClick={() => remove.mutate(i.id)}>Remove</button>
          </div>
        ))}
      </section>
      <section className="e-panel stack" style={{ gap: '0.75rem' }}>
      <h2 className="e-heading">Or upload receipts</h2>
      <div className="row">
        <div className="chips">
          <button type="button" aria-pressed={vendor === 'openai'} onClick={() => setVendor('openai')}>OpenAI</button>
          <button type="button" aria-pressed={vendor === 'anthropic'} onClick={() => setVendor('anthropic')}>Anthropic</button>
        </div>
        <label className="button primary" style={{ cursor: 'pointer' }}>
          Add receipts
          <input type="file" accept="application/pdf,image/png,image/jpeg" multiple hidden
            onChange={(e) => { const f = Array.from(e.target.files ?? []); if (f.length) send.mutate(f); e.target.value = '' }} />
        </label>
      </div>
      {send.isPending ? <p className="muted" data-testid="invoices-progress">Reading {Math.min(progress.done + 1, progress.total)} of {progress.total}…</p> : null}
      {!send.isPending && result ? <p data-testid="invoices-summary">{summaryLine(result)}</p> : null}
      {!send.isPending && result && result.failed.length ? (
        <div className="error-box" role="alert" data-testid="invoices-failed">
          <div>{result.failed.length} failed: {result.failed.map((f) => f.name).join(', ')}</div>
          {result.failed.map((f, i) => <div key={i}>{f.name}: {f.reason}</div>)}
        </div>
      ) : null}
      {send.error ? <ErrorBox error={send.error} /> : null}
      </section>
      {list.isLoading ? <Loading /> : list.error ? <ErrorBox error={list.error} /> : list.data!.rows.length === 0 ? (
        <Empty>No receipts yet. Download them from your OpenAI or Anthropic billing page.</Empty>
      ) : (
        <div className="e-panel table-wrap">
          <table className="stack-narrow">
            <thead><tr><th>Vendor</th><th>Invoice</th><th>Date</th><th className="right">Amount</th><th className="right">Points</th><th>Status</th></tr></thead>
            <tbody>
              {list.data!.rows.map((i) => (
                <tr key={i.id}>
                  <td>{i.vendor}</td>
                  <td className="mono">{i.invoice_number ?? ''}</td>
                  <td>{i.invoice_date ? day(i.invoice_date) : ''}</td>
                  <td className="right">{i.amount_cents != null ? cents(i.amount_cents) : ''}</td>
                  <td className="right" data-label="Points">{i.status === 'counted' && (i.earning_cents ?? i.amount_cents) ? (Math.floor((i.earning_cents ?? i.amount_cents ?? 0) / 100) * 4).toLocaleString() : ''}</td>
                  <td data-wide>{LABEL[i.status] ?? i.status}{i.reason ? <div className="muted" style={{ fontSize: '0.8125rem' }}>{i.reason}</div> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
